import React, { useState, useEffect, useRef } from 'react';
import { Concept, VisualMockup } from '../../../../../////types/ConceptsTypes';
import { motion, AnimatePresence } from 'motion/react';
import {
  Check,
  MessageCircle,
  ArrowRight,
  AlertTriangle,
  ShieldCheck,
  Smile,
  Frown,
  Clock,
  Zap,
  Lock,
} from 'lucide-react';
import './ConceptPreview.scss';
import IndustryShowcase from '../IndustryShowcase/IndustryShowcase';
import SalesPage from '../Sales/Sales';
import AgentMarketingSection from '../../Home/Heromarketing/AgentMarketingSection';

export const Instagram = ({ size = 24, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

interface ConceptPreviewProps {
  concept: Concept;
  simulationParams: {
    typingSpeed: number;
    showPlatformBadges: boolean;
    simulatedTraffic: number;
  };
}

export default function ConceptPreview({ concept, simulationParams }: ConceptPreviewProps) {
  const [cycleIndex, setCycleIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentWord = concept.typingWords[wordIndex % concept.typingWords.length];
  const [beforeVsAfterActive, setBeforeVsAfterActive] = useState<'before' | 'after'>('after');
  const [selectedIndustry, setSelectedIndustry] = useState<'Hospitality' | 'Retail' | 'Fintech' | 'Support'>('Hospitality');
  const [monthlyInquiries, setMonthlyInquiries] = useState(8000);
  const [costPerTicket, setCostPerTicket] = useState(5.5);
  const [activeBentoChannel, setActiveBentoChannel] = useState<'whatsapp' | 'instagram' | 'webchat'>('whatsapp');
  const [bentoSimResult, setBentoSimResult] = useState<string | null>(null);
  const [activeJourneyStep, setActiveJourneyStep] = useState(0);

  useEffect(() => {
    setTypedText('');
    setIsDeleting(false);
    setWordIndex(0);
    setCycleIndex(0);
  }, [concept.id]);

  useEffect(() => {
    const handleTyping = () => {
      if (!isDeleting) {
        setTypedText((prev) => currentWord.substring(0, prev.length + 1));
        if (typedText === currentWord) {
          if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
          typingTimerRef.current = setTimeout(() => setIsDeleting(true), 1800);
          return;
        }
      } else {
        setTypedText((prev) => currentWord.substring(0, prev.length - 1));
        if (typedText === '') {
          setIsDeleting(false);
          setWordIndex((prev) => prev + 1);
          setCycleIndex((prev) => (prev + 1) % concept.visualMockups.length);
          return;
        }
      }
      typingTimerRef.current = setTimeout(handleTyping, isDeleting ? 30 : 60);
    };
    typingTimerRef.current = setTimeout(handleTyping, isDeleting ? 30 : 100);
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [typedText, isDeleting, currentWord, concept.id, concept.visualMockups.length]);

  const currentCost = monthlyInquiries * costPerTicket;
  const caellCost = monthlyInquiries * 0.45;
  const monthlySavings = currentCost - caellCost;
  const hoursReclaimed = Math.round((monthlyInquiries * 8) / 60);

  const activeMockup: VisualMockup =
    concept.visualMockups[cycleIndex % concept.visualMockups.length] || concept.visualMockups[0];

  const platformBg =
    activeMockup.platform === 'whatsapp'
      ? '#25D366'
      : activeMockup.platform === 'instagram'
      ? '#DD2A7B'
      : '#2563EB';

  return (
    <div className="cp-root">
      {/* HERO */}
      <section className="cp-hero">
        <div className="cp-container cp-hero__grid">
          {/* Left */}
          <div className="cp-hero__left">
            <div className="cp-badge">
              <span className="cp-badge__dot" />
              24/7 Digital Workforce
            </div>
            <h1 className="cp-hero__h1">
              {concept.heroHeadlinePrefix}
              <br />
              <span className="cp-hero__typed">
                {typedText}
                <span className="cp-cursor">|</span>
              </span>
            </h1>
            <p className="cp-hero__sub">
              {concept.tagline}. A secure, deterministic AI layer deployed natively inside your
              customer channels — with zero hallucinations.
            </p>
            <div className="cp-hero__ctas">
              <button className="cp-btn-primary cp-btn-primary--lg">
                {concept.heroCTA} <ArrowRight size={16} />
              </button>
              <div className="cp-social">
                <div className="cp-avatars">
                  {[
                    { label: 'JD', cls: 'cp-avatar--blue' },
                    { label: 'SR', cls: 'cp-avatar--green' },
                    { label: 'AM', cls: 'cp-avatar--amber' },
                    { label: '+40', cls: 'cp-avatar--gray' },
                  ].map((a, i) => (
                    <div key={i} className={`cp-avatar ${a.cls}`}>
                      {a.label}
                    </div>
                  ))}
                </div>
                <span className="cp-social__label">
                  Used by leading
                  <br />
                  growth teams
                </span>
              </div>
            </div>
            <div className="cp-kpis">
              <div className="cp-kpi">
                <span className="cp-kpi__label">SPEED TO RESOLVE</span>
                <span className="cp-kpi__value">&lt; 4 Seconds</span>
              </div>
              <div className="cp-kpi">
                <span className="cp-kpi__label">SLA COMPLETION</span>
                <span className="cp-kpi__value">99.4% Automated</span>
              </div>
              <div className="cp-kpi">
                <span className="cp-kpi__label">OVERHEAD DROPS</span>
                <span className="cp-kpi__value cp-kpi__value--green">Slashed by 70%</span>
              </div>
            </div>
          </div>

          {/* Right — Phone Frame (unique protected classes) */}
          <div className="cp-hero__right">
            {simulationParams.showPlatformBadges && (
              <div className="cp-sim-badge">
                <span className="cp-sim-badge__dot" />
                Active iPhone Frame Simulator
              </div>
            )}

            <div className="cp-phone-frame">
              <div className="cp-phone-shell">
                <div className="cp-phone-notch" />
                <div className="cp-phone-screen">
                  {/* Header */}
                  <div className="cp-phone-header">
                    <div className="cp-phone-avatar" style={{ background: platformBg }}>
                      {activeMockup.platform === 'whatsapp'
                        ? 'W'
                        : activeMockup.platform === 'instagram'
                        ? 'I'
                        : 'C'}
                    </div>
                    <div className="cp-phone-contact">
                      <div className="cp-phone-name">{activeMockup.title}</div>
                      <div className="cp-phone-status">● Active Now</div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="cp-phone-messages">
                    <div className="cp-bubble cp-bubble-user">
                      <span className="cp-bubble-tag">PROSPECT QUERY</span>
                      {activeMockup.userQuery}
                    </div>
                    <div className="cp-bubble cp-bubble-ai">
                      <span className="cp-bubble-tag cp-bubble-tag-ai">✨ CAEL AI RESPONSE</span>
                      {activeMockup.botResponse}
                    </div>
                  </div>

                  {/* Outcome */}
                  <div className="cp-phone-outcome">
                    <span className="cp-phone-outcome-label">{activeMockup.outcomeBadge}</span>
                    <span className="cp-phone-outcome-sub">Secure Transaction Confirmed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div className="cp-float cp-float--left">
              <div className="cp-float__eyebrow">REAL-TIME ROI</div>
              <div className="cp-float__amount">
                ${(simulationParams.simulatedTraffic * 105).toLocaleString()}
                <span className="cp-float__delta">+18%</span>
              </div>
              <div className="cp-float__sub">Capturing live visitor interest</div>
            </div>

            <div className="cp-float cp-float--right">
              <div className="cp-float__icon">
                <Instagram size={15} color="#DD2A7B" />
              </div>
              <div>
                <div className="cp-float__title">Dynamic Multi-Channel</div>
                <div className="cp-float__sub">Capturing 100% of DMs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <IndustryShowcase />

      {/* SECTIONS */}
      <div className="cp-container cp-sections">
        {/* ROI */}
        {concept.id === 'roi-calculator' && (
          <section className="cp-section">
            <div className="cp-section__head">
              <span className="cp-eyebrow">ROI Calculator</span>
              <h2 className="cp-section__h2">Project your cost reduction</h2>
              <p className="cp-section__sub">
                Adjust your current support volume to see the financial impact.
              </p>
            </div>
            <div className="cp-card cp-roi">
              <div className="cp-roi__inputs">
                <div className="cp-field">
                  <div className="cp-field__row">
                    <label>Monthly inbound queries</label>
                    <strong>{monthlyInquiries.toLocaleString()}</strong>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={monthlyInquiries}
                    onChange={(e) => setMonthlyInquiries(Number(e.target.value))}
                    className="cp-range"
                  />
                </div>
                <div className="cp-field">
                  <div className="cp-field__row">
                    <label>Agent cost per ticket</label>
                    <strong>${costPerTicket.toFixed(2)}</strong>
                  </div>
                  <input
                    type="range"
                    min="1.50"
                    max="15.00"
                    step="0.50"
                    value={costPerTicket}
                    onChange={(e) => setCostPerTicket(Number(e.target.value))}
                    className="cp-range"
                  />
                  <span className="cp-field__hint">
                    Includes salaries, licensing, overhead and office costs.
                  </span>
                </div>
              </div>
              <div className="cp-roi__outputs">
                <div className="cp-roi__stats">
                  <div className="cp-stat">
                    <span className="cp-stat__label">Current spend</span>
                    <span className="cp-stat__val">${currentCost.toLocaleString()}</span>
                    <span className="cp-stat__sub">per month</span>
                  </div>
                  <div className="cp-stat cp-stat--green">
                    <span className="cp-stat__label">With Cael AI</span>
                    <span className="cp-stat__val">${caellCost.toLocaleString()}</span>
                    <span className="cp-stat__sub">per month</span>
                  </div>
                </div>
                <div className="cp-roi__savings">
                  <span className="cp-roi__savings-label">Monthly savings</span>
                  <span className="cp-roi__savings-val">
                    ${Math.round(monthlySavings).toLocaleString()}
                  </span>
                  <p className="cp-roi__savings-sub">
                    That's <strong>{hoursReclaimed} productive hours</strong> reclaimed per month.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BENTO */}
        {concept.id === 'glassmorphic-bento' && (
          <section className="cp-section">
            <div className="cp-section__head">
              <span className="cp-eyebrow">Platform capabilities</span>
              <h2 className="cp-section__h2">Built for modern operations</h2>
              <p className="cp-section__sub">Every module is purpose-built for enterprise workflows.</p>
            </div>
            <div className="cp-bento">
              <div className="cp-card cp-bento__card cp-bento__wide">
                <div className="cp-bento__icon-row">
                  <MessageCircle size={16} color="#2563EB" />
                  <span className="cp-eyebrow">Omnichannel router</span>
                </div>
                <h3 className="cp-bento__h3">Deploy across all touchpoints simultaneously</h3>
                <p className="cp-bento__p">
                  Unified presence across WhatsApp, Instagram, and web with one configuration.
                </p>
                <div className="cp-tabs">
                  {['whatsapp', 'instagram', 'webchat'].map((c) => (
                    <button
                      key={c}
                      onClick={() => setActiveBentoChannel(c as any)}
                      className={`cp-tab${activeBentoChannel === c ? ' cp-tab--active' : ''}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div className="cp-card cp-bento__card cp-bento__narrow">
                <div className="cp-bento__icon-row">
                  <Lock size={16} color="#059669" />
                  <span className="cp-eyebrow cp-eyebrow--green">Brand protection</span>
                </div>
                <h3 className="cp-bento__h3">100% deterministic compliance</h3>
                <p className="cp-bento__p">Never hallucinates pricing, scripts, or policy definitions.</p>
                <div className="cp-compliance-badge">
                  <ShieldCheck size={13} /> Audited security framework
                </div>
              </div>

              <div className="cp-card cp-bento__card cp-bento__narrow">
                <div className="cp-bento__icon-row">
                  <Zap size={16} color="#D97706" />
                  <span className="cp-eyebrow cp-eyebrow--amber">Conversion trigger</span>
                </div>
                <h3 className="cp-bento__h3">Cart recovery simulator</h3>
                <p className="cp-bento__p">
                  Trigger a simulated abandonment event to see resolution in action.
                </p>
                <button
                  onClick={() => {
                    setBentoSimResult(
                      'Recovery triggered — shopper offered sizing advice, discount applied, checkout completed in 4.1s.'
                    );
                    setTimeout(() => setBentoSimResult(null), 7000);
                  }}
                  className="cp-sim-btn"
                >
                  Simulate abandonment event
                </button>
                <AnimatePresence>
                  {bentoSimResult && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="cp-sim-result"
                    >
                      ✓ {bentoSimResult}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="cp-card cp-bento__card cp-bento__wide cp-bento__uptime-row">
                <div>
                  <div className="cp-bento__icon-row">
                    <Clock size={16} color="#5A6070" />
                    <span className="cp-eyebrow">24/7 availability</span>
                  </div>
                  <h3 className="cp-bento__h3">Always on, consistent performance</h3>
                  <p className="cp-bento__p">
                    Maintains speed during peak hours, seasonal events, and public holidays.
                  </p>
                </div>
                <div className="cp-uptime">
                  <span className="cp-uptime__val">100%</span>
                  <span className="cp-uptime__sub">Uptime SLA</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* JOURNEY */}
        {concept.id === 'customer-journey' && (
          <section className="cp-section">
            <div className="cp-section__head">
              <span className="cp-eyebrow">Resolution flow</span>
              <h2 className="cp-section__h2">Query to resolution in seconds</h2>
              <p className="cp-section__sub">
                How Cael AI handles every inbound interaction from first contact to confirmed
                outcome.
              </p>
            </div>
            <div className="cp-journey">
              {[
                {
                  n: '01',
                  Icon: MessageCircle,
                  title: 'Query received',
                  desc: 'Incoming message acknowledged instantly.',
                  status: 'Instant acknowledgement',
                },
                {
                  n: '02',
                  Icon: ShieldCheck,
                  title: 'Record matched',
                  desc: 'Customer identified and live inventory checked.',
                  status: 'Verified in 0.4s',
                },
                {
                  n: '03',
                  Icon: Zap,
                  title: 'Answer delivered',
                  desc: 'Tailored links or booking codes with deterministic accuracy.',
                  status: '99% precision',
                },
                {
                  n: '04',
                  Icon: Smile,
                  title: 'Transaction closed',
                  desc: 'Payment confirmed, satisfaction score recorded.',
                  status: '5-star rating logged',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveJourneyStep(idx)}
                  className={`cp-journey__step${
                    activeJourneyStep === idx ? ' cp-journey__step--active' : ''
                  }`}
                >
                  <div className="cp-journey__top">
                    <span className="cp-journey__num">{item.n}</span>
                    <div
                      className={`cp-journey__icon${
                        activeJourneyStep === idx ? ' cp-journey__icon--active' : ''
                      }`}
                    >
                      <item.Icon size={14} />
                    </div>
                  </div>
                  <h4 className="cp-journey__title">{item.title}</h4>
                  <p className="cp-journey__desc">{item.desc}</p>
                  <div className="cp-journey__status">{item.status}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}